import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { useRoutes } from '../hooks/useRoutes';
import { useAuth } from '../auth';
import { AppHeader } from '../components/AppHeader';
import { useAppModal } from '../components/Modal';
import { GameGrid } from '../components/GameGrid';
import { Numpad } from '../components/Numpad';
import { SettingsPopup, type SettingToggle } from '../components/SettingsPopup';
import { Confetti } from '../components/Confetti';
import { useGameBoard, formatTime, isGiven, clearAutoNotes, toggleNoteLocal } from '../hooks/useGameBoard';
import { GameStatusBar } from '../components/GameStatusBar';
import { DIFF_COLORS } from '../theme/difficulty';
import { useGameEffects } from '../hooks/useGameEffects';
import { useGameLogger } from '../hooks/useGameLogger';
import { useScrollLock } from '../hooks/useScrollLock';
import {
  takeOfflinePuzzle, getActiveGame, saveActiveGame, clearActiveGame, finalizeActiveGame,
  type OfflineTicket, type ActiveOfflineGame,
} from '../offline/offlineDb';
import { refillPoolIfNeeded } from '../offline/poolManager';
import { syncPendingGames } from '../offline/syncManager';
import { AchievementToast } from '../components/AchievementToast';
import { LevelBadge } from '../components/LevelBadge';
import { ComboCounter } from '../components/ComboCounter';
import { CoachModal } from '../components/CoachModal';
import { AdBanner, AD_SLOTS } from '../components/AdBanner';
import { useAdRewarded } from '../components/AdRewarded';
import './GamePage.css';

import { getHintBudget, techniqueSlug, techniqueLevel, explainStep as explainStepText, placeLocal, eraseLocal, hintLocal } from '@sudoku/shared';
import { track, createAttemptId, touchActivity } from '../lib/analytics';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export function GamePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, locale } = useI18n();
  const { r } = useRoutes();
  const { token, user, loading: authLoading } = useAuth();
  const { showAlert, showConfirm, showChoice, modalElement } = useAppModal();
  const [gameState, setGameState] = useState<'menu' | 'playing' | 'finished' | 'failed'>('menu');
  const [newAchievements, setNewAchievements] = useState<string[]>([]);
  const [isPersonalBest, setIsPersonalBest] = useState(false);
  const [xpInfo, setXpInfo] = useState<{ delta: number; total_xp: number; old_level: number; new_level: number; leveled_up: boolean; xp_into_level: number; xp_for_next: number } | null>(null);
  const [isRanked, setIsRanked] = useState(() => searchParams.get('mode') === 'ranked');
  const [targetSeconds, setTargetSeconds] = useState<number | null>(null);
  const [showRankedTargetInfo, setShowRankedTargetInfo] = useState(false);
  const [rankedResult, setRankedResult] = useState<{ delta: number; old_rating: number; new_rating: number; target_seconds: number } | null>(null);
  const [combo, setCombo] = useState(0);
  const [gameId, setGameId] = useState('');
  const [difficulty, setDifficulty] = useState(3);
  const hintBudget = getHintBudget(difficulty);
  const MAX_FREE_HINTS = hintBudget.free;
  const MAX_REWARDED_HINTS = hintBudget.rewarded;
  const [loading, setLoading] = useState(false);
  const [errorMode, setErrorMode] = useState(true);
  const [analyzed, setAnalyzed] = useState(false);
  const [showCoach, setShowCoach] = useState(false);
  const fx = useGameEffects();
  const log = useGameLogger(gameId || null, user?.id ?? null);
  const [offlineMode, setOfflineMode] = useState(false);
  const [offlineSolution, setOfflineSolution] = useState<number[][] | null>(null);
  const [offlineTicket, setOfflineTicket] = useState<OfflineTicket | null>(null);
  const [offlineOwnerUserId, setOfflineOwnerUserId] = useState<string | null>(null);
  const [bonusHints, setBonusHints] = useState(0);
  const [showBonusHintPrompt, setShowBonusHintPrompt] = useState(false);
  const { loading: adLoading, startRewardedAd, available: rewardedAvailable } = useAdRewarded();
  const offlineStartedAtRef = useRef<number>(0);
  const offlineFinishedRef = useRef(false);

  // Product analytics: game_start, game_complete, game_failed
  const startedGameIdsRef = useRef(new Set<string>());
  const completedGameIdsRef = useRef(new Set<string>());
  const failedGameIdsRef = useRef(new Set<string>());
  const currentAttemptIdRef = useRef<string>(createAttemptId());

  useEffect(() => {
    if (gameState !== 'finished' && gameState !== 'failed') return;
    const currentId = gameId || 'current';
    const mode = isRanked ? 'ranked' : offlineMode ? 'offline' : 'solo';
    const attemptId = currentAttemptIdRef.current;
    if (gameState === 'finished' && !completedGameIdsRef.current.has(currentId)) {
      completedGameIdsRef.current.add(currentId);
      track('game_complete', { mode, difficulty }, { attempt_id: attemptId, user: false });
    } else if (gameState === 'failed' && !failedGameIdsRef.current.has(currentId)) {
      failedGameIdsRef.current.add(currentId);
      track('game_failed', { mode, difficulty }, { attempt_id: attemptId, user: false });
    }
  }, [gameState, gameId, difficulty, isRanked, offlineMode]);


  // Shared board hook — callback'ler ref ile stale closure onlenir
  const placeValueRef = useRef<(r: number, c: number, value: number) => void>(() => {});
  const eraseRef = useRef<(r: number, c: number) => void>(() => {});
  const gb = useGameBoard({
    isPlaying: gameState === 'playing',
    onPlace: useCallback((r: number, c: number, v: number) => {
      touchActivity();
      placeValueRef.current(r, c, v);
    }, []),
    onErase: useCallback((r: number, c: number) => {
      touchActivity();
      eraseRef.current(r, c);
    }, []),
  });

  useScrollLock();

  useEffect(() => {
    if (!showRankedTargetInfo) return;
    const timer = window.setTimeout(() => setShowRankedTargetInfo(false), 4200);
    return () => window.clearTimeout(timer);
  }, [showRankedTargetInfo]);

  // URL'den resume param ile aktif oyunu devam ettir
  const resumeStartedRef = useRef(false);
  useEffect(() => {
    const resumeId = searchParams.get('resume');
    if (!resumeId || resumeStartedRef.current || !token) return;
    resumeStartedRef.current = true;
    (async () => {
      try {
        const res = await fetch(`${API}/game/resume`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => null);
        if (!data?.active || data.active.game_id !== resumeId) return;
        const a = data.active;
        gb.resetBoard(a.puzzle, a.board, a.notes, {
          elapsed: a.elapsed_time,
          mistakes: a.mistakes,
          hintsUsed: a.hints_used,
        });
        setBonusHints(a.rewarded_bonus_hints ?? 0);
        setIsRanked(!!a.is_ranked);
        setTargetSeconds(a.is_ranked && typeof a.target_seconds === 'number' ? a.target_seconds : null);
        setDifficulty(a.difficulty);
        setOfflineMode(false);
        setOfflineSolution(null);
        setGameId(a.game_id);
        offlineStartedAtRef.current = Date.now();
        offlineFinishedRef.current = false;

        let restoredAttempt: string | null = null;
        try {
          restoredAttempt = sessionStorage.getItem(`sudoku_game_attempt_${a.game_id}`);
          if (!restoredAttempt) {
            const raw = sessionStorage.getItem('sudoku_active_game_attempt');
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed?.gameId === a.game_id && typeof parsed?.attemptId === 'string') {
                restoredAttempt = parsed.attemptId;
              }
            }
          }
        } catch {}
        if (!restoredAttempt) {
          restoredAttempt = createAttemptId();
          try {
            sessionStorage.setItem(`sudoku_game_attempt_${a.game_id}`, restoredAttempt);
          } catch {}
        }
        currentAttemptIdRef.current = restoredAttempt;
        startedGameIdsRef.current.add(a.game_id);

        setGameState('playing');
      } catch (err) {
        console.error('Resume hatası:', err);
      }
    })();
  }, [searchParams, token, gb]);

  // URL'den puzzle parametresi ile replay başlat
  const replayStartedRef = useRef(false);
  useEffect(() => {
    const puzzleStr = searchParams.get('puzzle');
    const diffStr = searchParams.get('difficulty');
    const solStr = searchParams.get('solution');
    if (!puzzleStr || puzzleStr.length !== 81 || replayStartedRef.current) return;
    replayStartedRef.current = true;
    const puz: (number | null)[][] = [];
    for (let r = 0; r < 9; r++) {
      const row: (number | null)[] = [];
      for (let c = 0; c < 9; c++) {
        const v = parseInt(puzzleStr[r * 9 + c], 10);
        row.push(v > 0 ? v : null);
      }
      puz.push(row);
    }
    let solution: number[][] | undefined;
    if (solStr && solStr.length === 81) {
      solution = [];
      for (let r = 0; r < 9; r++) {
        const row: number[] = [];
        for (let c = 0; c < 9; c++) {
          row.push(parseInt(solStr[r * 9 + c], 10));
        }
        solution.push(row);
      }
    }
    const diff = diffStr ? parseInt(diffStr, 10) : 3;
    applyNewGame(puz, diff, { offline: true, solution: solution as any });
  }, [searchParams]);

  const applyNewGame = (puz: (number | null)[][], diff: number, opts: { offline: boolean; gameId?: string; solution?: number[][]; targetSeconds?: number | null; ticket?: OfflineTicket }) => {
    gb.resetBoard(puz);
    setDifficulty(diff);
    setOfflineMode(opts.offline);
    setOfflineSolution(opts.solution || null);
    setOfflineTicket(opts.ticket || null);
    // Sahiplik yalnızca oyun BAŞLARKEN bağlanır — sonradan hesap değişse de bu
    // oyunun sonucu her zaman o anki kullanıcıya (ya da anonime) ait kalır.
    setOfflineOwnerUserId(opts.offline ? (user?.id ?? null) : null);
    const actualGameId = opts.gameId || (opts.ticket?.pack_item_id ? `offline_${opts.ticket.pack_item_id}` : `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
    setGameId(actualGameId);
    setTargetSeconds(opts.targetSeconds ?? null);
    setBonusHints(0);
    offlineStartedAtRef.current = Date.now();
    offlineFinishedRef.current = false;
    setGameState('playing');

    if (!startedGameIdsRef.current.has(actualGameId)) {
      startedGameIdsRef.current.add(actualGameId);
      const attemptId = createAttemptId();
      currentAttemptIdRef.current = attemptId;
      try {
        sessionStorage.setItem(`sudoku_game_attempt_${actualGameId}`, attemptId);
        sessionStorage.setItem('sudoku_active_game_attempt', JSON.stringify({ gameId: actualGameId, attemptId }));
      } catch {}
      track('game_start', {
        mode: isRanked ? 'ranked' : opts.offline ? 'offline' : 'solo',
        difficulty: diff,
      }, { attempt_id: attemptId });
    }

    // game_start logu — gameId henüz güncellenemediği için opts.gameId ile manüel gönder
    if (opts.gameId) {
      fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/debug/game-action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
        body: JSON.stringify({ session_id: crypto.randomUUID(), game_id: opts.gameId, user_id: user?.id ?? 'anon', ts: Date.now(), type: 'game_start', extra: { difficulty: diff, offline: opts.offline } }),
      }).catch(() => {});
    }
  };

  // Kaydedilmiş bir offline oyunu (uygulama kapanmadan önce yarım kalmış)
  // olduğu gibi geri yükler — yeni bulmaca çekilmez.
  const resumeActiveOfflineGame = (active: ActiveOfflineGame) => {
    gb.resetBoard(active.puzzle, active.board, active.notes, {
      elapsed: active.elapsed_seconds,
      mistakes: active.mistakes,
      hintsUsed: active.hints_used,
      score: active.score,
    });
    setDifficulty(active.difficulty);
    setOfflineMode(true);
    setOfflineSolution(active.solution);
    setOfflineTicket(active.ticket);
    setOfflineOwnerUserId(active.owner_user_id);
    const resumeId = active.ticket?.pack_item_id
      ? `offline_${active.ticket.pack_item_id}`
      : `offline_resumed_${active.started_at || Date.now()}`;
    setGameId(resumeId);

    let restoredAttempt: string | null = null;
    try {
      restoredAttempt = sessionStorage.getItem(`sudoku_game_attempt_${resumeId}`);
      if (!restoredAttempt) {
        const raw = sessionStorage.getItem('sudoku_active_game_attempt');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.gameId === resumeId && typeof parsed?.attemptId === 'string') {
            restoredAttempt = parsed.attemptId;
          }
        }
      }
    } catch {}
    if (!restoredAttempt) {
      restoredAttempt = createAttemptId();
      try {
        sessionStorage.setItem(`sudoku_game_attempt_${resumeId}`, restoredAttempt);
      } catch {}
    }
    currentAttemptIdRef.current = restoredAttempt;
    startedGameIdsRef.current.add(resumeId);

    setTargetSeconds(null);
    setBonusHints(0);
    offlineStartedAtRef.current = Date.now() - active.elapsed_seconds * 1000;
    offlineFinishedRef.current = false;
    setGameState('playing');
  };

  // Cihazlar arası devamlılık — oyun state'ini periyodik olarak backend'e kaydet
  const lastSaveRef = useRef(0);
  useEffect(() => {
    if (gameState !== 'playing' || offlineMode || !gameId || !token) return;
    const save = () => {
      const now = Date.now();
      if (now - lastSaveRef.current < 4000) return;
      lastSaveRef.current = now;
      fetch(`${API}/game/save-progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          game_id: gameId,
          board: gb.board,
          notes: gb.notes,
          mistakes: gb.mistakes,
          hints_used: gb.hintsUsed,
          elapsed_time: gb.elapsed,
        }),
        keepalive: true,
      }).catch(() => {});
    };
    const iv = setInterval(save, 15000);
    // sayfa kapanırken/son durumda da kaydet
    const beforeUnload = () => { lastSaveRef.current = 0; save(); };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      clearInterval(iv);
      window.removeEventListener('beforeunload', beforeUnload);
      // kapanışta son kez kaydet
      lastSaveRef.current = 0;
      save();
    };
  }, [gameState, offlineMode, gameId, token, gb.board, gb.notes, gb.mistakes, gb.hintsUsed, gb.elapsed]);

  // Admin panelinin gerçek "şu an aktif" tespiti için hafif heartbeat — oyun
  // ekranı foreground'dayken periyodik ping. Skor/state DEĞİŞTİRMEZ, sadece
  // sunucudaki last_heartbeat_at'i günceller. Anonim oyunlar da (game_id
  // capability'siyle) çağırabilir — token opsiyonel. Offline modda hiç
  // gönderilmez (offline oyun sunucuda bilinmiyor, sunucudan "aktif" sayılamaz).
  useEffect(() => {
    if (gameState !== 'playing' || offlineMode || !gameId) return;
    const sendHeartbeat = () => {
      if (document.visibilityState !== 'visible') return;
      fetch(`${API}/game/heartbeat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ game_id: gameId }),
        keepalive: true,
      }).catch(() => {});
    };
    sendHeartbeat();
    const iv = setInterval(sendHeartbeat, 30000);
    const onVisibility = () => { if (document.visibilityState === 'visible') sendHeartbeat(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [gameState, offlineMode, gameId, token]);

  // Sekme gizlenince otomatik duraklat
  useEffect(() => {
    const handler = () => {
      if (document.hidden && gameState === 'playing') gb.setIsPaused(true);
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [gameState, gb.setIsPaused]);

  // Devam eden offline oyunun kalıcılığı — uygulama/sekme kapansa da ilerleme
  // kaybolmasın diye periyodik olarak (ve kapanırken) yerel DB'ye yazılır.
  const lastOfflineSaveRef = useRef(0);
  useEffect(() => {
    if (gameState !== 'playing' || !offlineMode || !offlineSolution || !offlineTicket) return;
    const save = () => {
      if (offlineFinishedRef.current) return; // oyun az önce bitti — active kaydı zaten silindi, geri getirme
      const now = Date.now();
      if (now - lastOfflineSaveRef.current < 4000) return;
      lastOfflineSaveRef.current = now;
      void saveActiveGame({
        puzzle: gb.puzzle,
        solution: offlineSolution,
        board: gb.board,
        notes: gb.notes,
        difficulty,
        mistakes: gb.mistakes,
        hints_used: gb.hintsUsed,
        score: gb.score,
        elapsed_seconds: gb.elapsed,
        started_at: offlineStartedAtRef.current,
        ticket: offlineTicket,
        owner_user_id: offlineOwnerUserId,
      }).catch(() => {}); // fire-and-forget — geçici IndexedDB hatası bir sonraki tick'te tekrar denenir
    };
    // İlk kaydı hemen yaz — yalnızca interval'a bırakılırsa (15sn) bulmaca havuzdan
    // atomik olarak tüketildikten sonraki pencerede uygulama öldürülürse hem
    // bulmaca hem ilerleme kaybolurdu, resume kaydı hiç oluşmazdı (Codex bulgusu, doğrulandı).
    save();
    const iv = setInterval(save, 15000);
    const beforeUnload = () => { lastOfflineSaveRef.current = 0; save(); };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      clearInterval(iv);
      window.removeEventListener('beforeunload', beforeUnload);
      lastOfflineSaveRef.current = 0;
      save();
    };
  }, [gameState, offlineMode, offlineSolution, offlineTicket, offlineOwnerUserId, difficulty, gb.puzzle, gb.board, gb.notes, gb.mistakes, gb.hintsUsed, gb.score, gb.elapsed]);

  // Yeni oyun
  const startGame = async (diff: number) => {
    setLoading(true);
    try {
      // Yarım kalmış bir offline oyun varsa (aynı hesap/anonim) devam etme seçeneği sun.
      // Dereceli başlatılıyorsa bu kontrol tamamen atlanır — Ranked asla offline'a düşmemeli.
      if (!isRanked) {
        const active = await getActiveGame(user?.id ?? null);
        if (active) {
          const wantsResume = await showConfirm(
            t.offline.resumePrompt, t.offline.resumeTitle, t.offline.resumeContinue, t.offline.resumeNew,
          );
          if (wantsResume) {
            resumeActiveOfflineGame(active);
            setLoading(false);
            return;
          }
          // "Yeni Oyun" seçildi — reddedilen kayıt tekrar sorulmasın diye silinir.
          await clearActiveGame(user?.id ?? null);
        }
      }

      if (navigator.onLine) {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API}/game/new`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ difficulty: diff, is_ranked: isRanked && !!token }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.game_id) {
          setRankedResult(null);
          applyNewGame(data.puzzle, diff, {
            offline: false,
            gameId: data.game_id,
            targetSeconds: data.is_ranked && typeof data.target_seconds === 'number' ? data.target_seconds : null,
          });
        }
      } else if (isRanked) {
        // Dereceli: hiçbir koşulda offline'a düşülmez — sadece hata gösterilir.
        await showAlert(t.common.connectionError, t.common.error);
        setLoading(false);
        return;
      } else {
        const p = await takeOfflinePuzzle(diff, user?.id ?? null);
        if (!p) {
          showAlert(t.offline.poolEmpty);
          setLoading(false);
          return;
        }
        applyNewGame(p.puzzle, p.difficulty, { offline: true, solution: p.solution, ticket: p.ticket });
      }
    } catch (err) {
      console.error('Oyun oluşturma hatası:', err);
      if (isRanked) {
        // Dereceli: hiçbir koşulda offline'a düşülmez — sadece hata gösterilir.
        await showAlert(t.common.connectionError, t.common.error);
        setLoading(false);
        return;
      }
      // Casual'da fetch hatası olursa offline'a düş
      const p = await takeOfflinePuzzle(diff, user?.id ?? null);
      if (p) {
        applyNewGame(p.puzzle, p.difficulty, { offline: true, solution: p.solution, ticket: p.ticket });
      } else {
        // Havuz da boşsa kullanıcı sessizce menüde kalmasın (Codex bulgusu, doğrulandı).
        showAlert(t.offline.poolEmpty);
      }
    }
    setLoading(false);
  };

  // URL'den autostart: ?difficulty=N (puzzle parametresi yoksa) — ana sayfadaki
  // difficulty sheet'ten gelen yönlendirme. Menü ekranını atlayıp doğrudan oyunu başlat.
  const autostartedRef = useRef(false);
  useEffect(() => {
    if (autostartedRef.current) return;
    if (gameState !== 'menu' || loading) return;
    if (searchParams.get('puzzle')) return; // replay path zaten çalışıyor
    if (searchParams.get('resume')) return; // resume path zaten çalışıyor
    const diffStr = searchParams.get('difficulty');
    if (!diffStr) return; // parametre yoksa menü ekranını göster
    const d = parseInt(diffStr, 10);
    if (!(d >= 1 && d <= 5)) return;
    // Dereceli bir deep-link'te auth henüz hydrate olmadıysa (token bilinmiyor)
    // startGame'e giden `isRanked && !!token` sahte-false üretip Dereceli oyunu
    // sessizce normal oyuna dönüştürebilirdi (Codex bulgusu, doğrulandı) — auth
    // netleşene kadar bekle, bu durumda tekrar tetiklenmesi için ref set edilmez.
    if (searchParams.get('mode') === 'ranked' && authLoading) return;
    // Auth netleşti ve gerçekten giriş yapılmamış: Dereceli sessizce normal
    // oyuna dönüşmesin — giriş sayfasına yönlendir (Codex bulgusu, doğrulandı).
    if (searchParams.get('mode') === 'ranked' && !token) {
      autostartedRef.current = true;
      navigate(r('auth'), { replace: true });
      return;
    }
    autostartedRef.current = true;
    startGame(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, loading, searchParams, authLoading, token]);

  // Hiçbir parametre olmadan (bare /oyna) doğrudan girilirse — kendi içindeki
  // eski zorluk menüsünü göstermek yerine ana sayfaya yönlendirip oradaki
  // DifficultySheet'i aç (tek, tutarlı bir zorluk seçme deneyimi).
  // Prerender sırasında ise /play rotalarının kendi temiz SEO ve içerik çıktısını üretmesi için yönlendirme yapılmaz.
  const isPrerender = typeof window !== 'undefined' && Boolean((window as unknown as { __PRERENDER__?: boolean }).__PRERENDER__);
  const redirectedToHomeRef = useRef(false);
  useEffect(() => {
    if (isPrerender) return;
    if (redirectedToHomeRef.current || autostartedRef.current) return;
    if (gameState !== 'menu' || loading) return;
    if (searchParams.get('puzzle') || searchParams.get('resume') || searchParams.get('difficulty')) return;
    redirectedToHomeRef.current = true;
    navigate(`${r('home')}?openDifficulty=solo`, { replace: true });
  }, [gameState, loading, searchParams, navigate, r, isPrerender]);



  // Hücreye değer yaz
  const placeValue = async (_r: number, _c: number, value: number) => {
    if (!gb.selectedCell || gameState !== 'playing') return;
    const [sr, sc] = gb.selectedCell;
    if (isGiven(gb.puzzle, sr, sc)) return;
    // Zaten doğru yerleştirilmiş hücreye aynı değer girilirse işlem yapma
    if (gb.board[sr][sc] !== null) return;

    // ─── OFFLINE PATH — sunucuya sormadan, local-authoritative motor (shared/src/logic.ts) ───
    if (offlineMode) {
      if (gb.noteMode) {
        if (gb.showCandidates) return; // Adaylar açıkken not editleme kapalı (Approach A)
        gb.setNotes(prev => toggleNoteLocal(prev, sr, sc, value));
        log({ type: 'note', row: sr, col: sc, value });
        return;
      }
      if (!offlineSolution) return;
      const result = placeLocal(gb.puzzle, gb.board, gb.notes, offlineSolution, sr, sc, value, gb.autoCleanNotes);
      if (result.invalid) return;
      log({ type: 'place', row: sr, col: sc, value, result: result.correct ? 'correct' : 'wrong', mistakes: result.correct ? gb.mistakes : gb.mistakes + 1, elapsed: gb.elapsed });
      if (result.correct) {
        gb.setScore(prev => prev + 10);
        gb.setBoard(result.board);
        gb.setNotes(result.notes);
        fx.playCorrect();
        fx.vibrateCorrect();
        if (result.isComplete) {
          fx.triggerComplete(sr, sc);
          fx.playComplete();
          fx.vibrateComplete();
          setGameState('finished');
          if (gb.timerRef.current) clearInterval(gb.timerRef.current);
          void finalizeOfflineGame(true);
        } else {
          fx.triggerCorrect(sr, sc, result.board, value);
        }
      } else {
        if (errorMode) {
          const newMistakes = gb.mistakes + 1;
          gb.setMistakes(newMistakes);
          gb.setScore(prev => Math.max(0, prev - 5));
          setCombo(0);
          gb.setWrongCell([sr, sc]);
          setTimeout(() => gb.setWrongCell(null), 1000);
          fx.playWrong();
          fx.vibrateWrong();
          fx.triggerWrong(sr, sc);
          if (newMistakes >= 3) {
            setGameState('failed');
            if (gb.timerRef.current) clearInterval(gb.timerRef.current);
            void finalizeOfflineGame(false, { mistakes: newMistakes });
          }
        }
      }
      return;
    }

    if (gb.noteMode) {
      if (gb.showCandidates) return; // Adaylar açıkken not editleme kapalı (Approach A)
      // Optimistic local toggle — server yanıtını BEKLEME. Önceden (showCandidates
      // kapalıyken) /game/note yanıtı beklenip hücreye yazılıyordu; hızlı sil→ekle'de
      // iki async istek yarışıp sonradan gelen bayat yanıt eski notu geri getiriyordu.
      gb.setNotes(prev => toggleNoteLocal(prev, sr, sc, value));
      log({ type: 'note', row: sr, col: sc, value });
      if (!gb.showCandidates) {
        // Server'a fire-and-forget persist (resume için); yanıtı UI'a uygulama.
        fetch(`${API}/game/note`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ game_id: gameId, row: sr, col: sc, value }),
        }).catch(() => {});
      }
      return;
    }

    // Normal mod
    try {
      const res = await fetch(`${API}/game/place`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ game_id: gameId, row: sr, col: sc, value }),
      });
      if (res.status === 409) {
        // Backend restart/deploy sonrası session kaybolmuş — asla "yanlış cevap" sayma
        showAlert(t.game.sessionLost);
        void startGame(difficulty);
        return;
      }
      if (!res.ok) { console.error('place failed', res.status); return; }
      const data = await res.json();
      if (data.correct) {
        log({ type: 'place', row: sr, col: sc, value, result: 'correct', score: gb.score + 10, elapsed: gb.elapsed });
        gb.setScore(prev => prev + 10);
        setCombo(c => c + 1);
        const updatedBoard = gb.board.map((row, rr) => row.map((cell, cc) => (rr === sr && cc === sc ? value : cell)));
        gb.setBoard(() => updatedBoard.map(row => [...row]));
        gb.setNotes(prev => {
          if (gb.autoCleanNotes) return clearAutoNotes(prev, sr, sc, value);
          const n = prev.map(row => row.map(cell => [...cell])); n[sr][sc] = []; return n;
        });
        fx.playCorrect();
        fx.vibrateCorrect();
        if (data.is_finished) {
          fx.triggerComplete(sr, sc);
          fx.playComplete();
          fx.vibrateComplete();
          setGameState('finished');
          if (gb.timerRef.current) clearInterval(gb.timerRef.current);
          if (data.new_achievements?.length) setNewAchievements(data.new_achievements);
          if (data.is_personal_best) setIsPersonalBest(true);
          if (data.xp) setXpInfo(data.xp);
          if (data.ranked) setRankedResult(data.ranked);
        } else {
          fx.triggerCorrect(sr, sc, updatedBoard, value);
        }
      } else {
        if (errorMode) {
          const newMistakes = data.mistakes;
          log({ type: 'place', row: sr, col: sc, value, result: 'wrong', mistakes: newMistakes, elapsed: gb.elapsed });
          gb.setMistakes(newMistakes);
          gb.setScore(prev => Math.max(0, prev - 5));
          setCombo(0);
          gb.setWrongCell([sr, sc]);
          setTimeout(() => gb.setWrongCell(null), 1000);
          fx.playWrong();
          fx.vibrateWrong();
          fx.triggerWrong(sr, sc);
          if (newMistakes >= 3) {
            setGameState('failed');
            if (gb.timerRef.current) clearInterval(gb.timerRef.current);
            // Failed cevabında ranked bilgisi varsa son durumda set et (3. hata kaydetme yanıtı)
            if (data.ranked) setRankedResult(data.ranked);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };
  placeValueRef.current = placeValue;

  // Offline oyun bitince aktif-kayıt→bekleyen-kuyruk geçişi TEK transaction'da (offlineDb.finalizeActiveGame) + varsa sync et.
  // `overrides`: çağıran taraf bu senkron işlemin İÇİNDE gb.setMistakes/gb.setHintsUsed
  // çağırdıysa (React state güncellemesi bir sonraki render'a kadar `gb.mistakes`/
  // `gb.hintsUsed`'a yansımaz), güncel değeri buradan geçirir — yoksa closure
  // içindeki bir önceki (bayat) değer kaydedilir.
  const finalizeOfflineGame = async (won: boolean, overrides?: { mistakes?: number; hintsUsed?: number }) => {
    if (offlineFinishedRef.current) return;
    offlineFinishedRef.current = true;
    if (!offlineSolution || !offlineTicket) return;
    const duration = Math.max(0, (Date.now() - offlineStartedAtRef.current) / 1000);
    try {
      await finalizeActiveGame({
        puzzle: gb.puzzle.map(row => [...row]),
        solution: offlineSolution.map(row => [...row]),
        difficulty,
        duration_seconds: duration,
        mistakes: overrides?.mistakes ?? gb.mistakes,
        hints_used: overrides?.hintsUsed ?? gb.hintsUsed,
        completed_at: Date.now(),
        won,
        ticket: offlineTicket,
        owner_user_id: offlineOwnerUserId,
      });
    } catch (e) {
      console.warn('Offline kuyruğa alma hatası:', e);
    }
    if (navigator.onLine && token) {
      void syncPendingGames(token, user?.id ?? null);
    }
    void refillPoolIfNeeded(token, user?.id ?? null);
  };

  // Silme
  const eraseCell = async (_r: number, _c: number) => {
    if (!gb.selectedCell || gameState !== 'playing') return;
    const [sr, sc] = gb.selectedCell;
    if (isGiven(gb.puzzle, sr, sc)) return;
    log({ type: 'erase', row: sr, col: sc });
    if (offlineMode) {
      gb.setBoard(prev => eraseLocal(gb.puzzle, prev, sr, sc));
      return;
    }
    try {
      await fetch(`${API}/game/erase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ game_id: gameId, row: sr, col: sc }),
      });
      gb.setBoard(prev => { const b = prev.map(row => [...row]); b[sr][sc] = null; return b; });
    } catch (err) {
      console.error(err);
    }
  };
  eraseRef.current = eraseCell;

  // İpucu
  const getHint = async () => {
    if (gameState !== 'playing') return;
    const isPremium = !!user?.is_premium;
    const hintLimit = MAX_FREE_HINTS + bonusHints;
    const hasHintRemaining = gb.hintsUsed < hintLimit;
    const canEarnRewardedHint = rewardedAvailable
      && !offlineMode
      && !isRanked
      && bonusHints < MAX_REWARDED_HINTS;
    if (!isPremium && !hasHintRemaining) {
      if (canEarnRewardedHint) setShowBonusHintPrompt(true);
      return;
    }
    log({ type: 'hint', elapsed: gb.elapsed, extra: { hintsUsed: gb.hintsUsed } });
    if (offlineMode) {
      if (!offlineSolution) return;
      const result = hintLocal(gb.board, offlineSolution);
      if (!result) return; // tahta zaten dolu
      const newHintsUsed = gb.hintsUsed + 1;
      gb.setBoard(result.board);
      gb.setHintsUsed(newHintsUsed);
      gb.setScore(prev => Math.max(0, prev - 20));
      gb.setSelectedCell([result.row, result.col]);
      track('hint_used', { mode: isRanked ? 'ranked' : offlineMode ? 'offline' : 'solo', difficulty }, { attempt_id: currentAttemptIdRef.current });
      if (result.isComplete) {
        fx.triggerComplete(result.row, result.col);
        fx.playComplete();
        fx.vibrateComplete();
        setGameState('finished');
        if (gb.timerRef.current) clearInterval(gb.timerRef.current);
        void finalizeOfflineGame(true, { hintsUsed: newHintsUsed });
      }
      return;
    }
    try {
      const res = await fetch(`${API}/game/hint/${gameId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (data.value) {
        gb.setBoard(prev => { const b = prev.map(row => [...row]); b[data.row][data.col] = data.value; return b; });
        gb.setHintsUsed(data.hints_used);
        gb.setScore(prev => Math.max(0, prev - 20));
        gb.setSelectedCell([data.row, data.col]);
        fx.playHint();
        track('hint_used', { mode: isRanked ? 'ranked' : offlineMode ? 'offline' : 'solo', difficulty }, { attempt_id: currentAttemptIdRef.current });
      }
    } catch (err) {
      console.error(err);
    }
  };

  // "Tek Adımı Açıkla": mevcut tahtadan bulunan ilk mantıksal adımı teknik+neden
  // ile gösterir, oyunu bitirmez. Aynı ipucu hakkını kullanır (getHint ile aynı
  // gating). Çevrimdışı modda kullanılamaz (backend'in solver'ına ihtiyaç duyar).
  const explainStep = async () => {
    if (gameState !== 'playing' || offlineMode) return;
    const isPremium = !!user?.is_premium;
    const hintLimit = MAX_FREE_HINTS + bonusHints;
    const hasHintRemaining = gb.hintsUsed < hintLimit;
    const canEarnRewardedHint = rewardedAvailable && !offlineMode && !isRanked && bonusHints < MAX_REWARDED_HINTS;
    if (!isPremium && !hasHintRemaining) {
      if (canEarnRewardedHint) setShowBonusHintPrompt(true);
      return;
    }
    try {
      const res = await fetch(`${API}/game/hint/${gameId}?explained=true`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (data.error) {
        await showAlert(
          data.error === 'hint_limit' ? t.game.hintLimit
            : data.error === 'no_step' ? t.game.explainedNoStep
            : data.error,
          t.game.hintTitle,
        );
        return;
      }
      if (data.applied) {
        gb.setBoard(prev => { const b = prev.map(row => [...row]); b[data.row][data.col] = data.value; return b; });
        gb.setSelectedCell([data.row, data.col]);
      }
      gb.setHintsUsed(data.hints_used);
      gb.setScore(prev => Math.max(0, prev - 20));
      fx.playHint();
      track('hint_used', { mode: isRanked ? 'ranked' : offlineMode ? 'offline' : 'solo', difficulty }, { attempt_id: currentAttemptIdRef.current });

      const slug = techniqueSlug(data.technique);
      const level = techniqueLevel(data.technique);
      const localizedName = slug ? (t.edu.techniques as any)[slug]?.name : undefined;
      await showAlert(
        explainStepText(data, locale),
        `${localizedName ?? data.technique} · ${t.difficulties[level]}`,
      );
    } catch (err) {
      console.error(err);
    }
  };

  // Rewarded ad handler
  const handleRewardedAdReward = async () => {
    if (!gameId || offlineMode) {
      setShowBonusHintPrompt(false);
      showAlert(t.ads.adFailed);
      return;
    }
    try {
      const res = await fetch(`${API}/game/rewarded-hint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ game_id: gameId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'rewarded_hint_failed');
      setBonusHints(Math.min(data.rewarded_bonus_hints ?? bonusHints + 1, MAX_REWARDED_HINTS));
      setShowBonusHintPrompt(false);
      showAlert(t.ads.bonusHintEarned);
    } catch (err) {
      console.error(err);
      setShowBonusHintPrompt(false);
      showAlert(t.ads.adFailed);
    }
  };

  const handleBonusHintWatchAd = async () => {
    const wasPaused = gb.isPaused;
    gb.setIsPaused(true);
    try {
      const result = await startRewardedAd();
      if (result === 'viewed') {
        await handleRewardedAdReward();
        return;
      }
      setShowBonusHintPrompt(false);
      if (result !== 'dismissed') showAlert(t.ads.adFailed);
    } finally {
      gb.setIsPaused(wasPaused);
    }
  };

  // Adaylar modu (Approach A): canlı overlay, client-side hesaplanır (sunucuya
  // gitmez, manuel notları ezmez). Açınca not modu kapanır (karşılıklı dışlayan).
  const toggleCandidates = () => {
    const next = !gb.showCandidates;
    gb.setShowCandidates(next);
    if (next) gb.setNoteMode(false);
  };

  // Numpad handler
  const handleNumpadClick = useCallback((n: number) => {
    gb.setHighlightDigit(n);
    placeValue(-1, -1, n);
  }, [placeValue]);

  // Settings
  const settings: SettingToggle[] = [
    { key: 'errorMode', labelOn: t.game.errorModeOn, labelOff: t.game.errorModeOff, value: errorMode, onToggle: () => setErrorMode(v => !v), helpText: t.game.errorModeHelp },
    { key: 'autoClean', labelOn: t.game.autoCleanOn, labelOff: t.game.autoCleanOff, value: gb.autoCleanNotes, onToggle: () => gb.setAutoCleanNotes(v => !v), helpText: t.game.autoCleanHelp },
    { key: 'highlight', labelOn: t.game.highlightOn, labelOff: t.game.highlightOff, value: gb.highlightEnabled, onToggle: () => gb.setHighlightEnabled(v => !v), helpText: t.game.highlightHelp },
    { key: 'cellHighlight', labelOn: t.game.cellHighlightOn, labelOff: t.game.cellHighlightOff, value: gb.cellHighlightEnabled, onToggle: () => gb.setCellHighlightEnabled(v => !v), helpText: t.game.cellHighlightHelp },
    { key: 'effects', labelOn: t.game.effectsOn, labelOff: t.game.effectsOff, value: fx.isEffectsEnabled, onToggle: fx.toggleEffects, helpText: t.game.effectsHelp },
    { key: 'sound', labelOn: t.game.soundOn, labelOff: t.game.soundOff, value: fx.isSoundEnabled, onToggle: fx.toggleSound, helpText: t.game.soundHelp },
  ];

  // ─── MENU ───
  if (gameState === 'menu') {
    return (
      <div className="game-page">
        {modalElement}
        <SEO path="/play" />
        <AppHeader onBack={() => navigate('/')} backLabel={t.common.backToHome} title={t.game.title} />
        {token && (
          <div className="ranked-toggle-row">
            <button
              className={`ranked-toggle-btn${isRanked ? ' active' : ''}`}
              onClick={() => setIsRanked(v => !v)}
              title={(t.game as any).rankedSoloHint ?? ''}
            >
              🏆 {t.game.rankedSolo} {isRanked ? `• ${t.common.on}` : `• ${t.common.off}`}
            </button>
          </div>
        )}
        {isRanked && token && (
          <div className="ranked-toggle-hint">
            {t.game.rankedSoloHint}
          </div>
        )}
        <div className="difficulty-select">
          {loading ? (
            <div className="loading-msg">{t.game.generating}</div>
          ) : (
            <>
              <h2>{t.game.chooseDifficulty}</h2>
              <div className="difficulty-grid">
                {([1, 2, 3, 4, 5] as const).map(d => (
                  <button
                    key={d}
                    className={`difficulty-btn diff-${d}`}
                    onClick={() => startGame(d)}
                  >
                    <span className="diff-name">{t.difficulties[d]}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── FAILED ───
  if (gameState === 'failed') {
    return (
      <div className="game-page">
        {modalElement}
        <AppHeader onBack={() => navigate('/')} backLabel={t.common.backToHome} title={t.game.gameOver} />
        <div className="finish-screen">
          <p className="game-over-msg">{t.game.tooManyMistakes}</p>
          {rankedResult && (
            <div className="finish-ranked-row ranked-loss">
              <div className="finish-ranked-delta">
                {rankedResult.delta} ELO
              </div>
              <div className="finish-ranked-sub">
                {rankedResult.old_rating} → <strong>{rankedResult.new_rating}</strong>
              </div>
            </div>
          )}
          <div className="finish-actions">
            <button onClick={() => startGame(difficulty)}>{t.game.sameLevel}</button>
            <button onClick={() => setGameState('menu')}>{t.game.changeDifficulty}</button>
            <button onClick={() => {
              // Bitiş ekranı: ORİJİNAL puzzle'ı gönder (çözülmüş/mevcut tahtayı değil)
              // ki solver baştan çözüm yolunu + teknikleri gösterebilsin.
              const boardStr = gb.puzzle.map(row => row.map(c => c ?? 0).join(',')).join(';');
              window.open(`${r('solver')}?board=${encodeURIComponent(boardStr)}`, '_blank');
            }}>{t.game.reviewSolution}</button>
            <button className="coach-btn" onClick={() => {
              if (!user) { navigate('/auth'); return; }
              setShowCoach(true);
            }}>🧠 {t.coach.title}{!user?.is_premium ? ' 🔒' : ''}</button>
          </div>
          <div style={{ marginTop: 16 }}><AdBanner slot={AD_SLOTS.gameEnd} /></div>
          {!user?.is_premium && (
            <div className="game-upsell-toast">
              <span>{t.ads.upsellToast}</span>
              <button onClick={() => navigate('/shop')}>Pro</button>
            </div>
          )}
        </div>
        <CoachModal
          open={showCoach}
          initialBoard={gb.puzzle.map(row => row.map(v => v ?? 0))}
          onClose={() => setShowCoach(false)}
          t={t}
        />
      </div>
    );
  }

  // ─── FINISHED ───
  if (gameState === 'finished') {
    return (
      <div className="game-page">
        {modalElement}
        <AchievementToast achievementIds={newAchievements} onDone={() => setNewAchievements([])} />
        <AppHeader onBack={() => navigate('/')} backLabel={t.common.backToHome} title={t.game.congratulations} />
        <div className="finish-screen">
          {isPersonalBest && (
            <div className="personal-best-banner">
              <span className="pb-icon">⚡</span>
              <span className="pb-label">{t.profile.personalBest}</span>
              <span className="pb-sub">{t.difficulties[difficulty]}</span>
            </div>
          )}
          {xpInfo && (
            <div className="finish-xp-row">
              <LevelBadge level={xpInfo.new_level} size="lg" />
              <div className="finish-xp-delta">
                +{xpInfo.delta} XP
              </div>
              {xpInfo.leveled_up && (
                <div className="finish-level-up-pill">
                  {t.profile.levelUp}
                </div>
              )}
            </div>
          )}
          {rankedResult && (
            <div className={`finish-ranked-row ${rankedResult.delta >= 0 ? 'ranked-win' : 'ranked-loss'}`}>
              <div className="finish-ranked-delta">
                {rankedResult.delta >= 0 ? '+' : ''}{rankedResult.delta} ELO
              </div>
              <div className="finish-ranked-sub">
                {rankedResult.old_rating} → <strong>{rankedResult.new_rating}</strong>
              </div>
            </div>
          )}
          <div className="finish-stats">
            <div className="stat score-stat">
              <span className="stat-label">{t.room.scoreLabel}</span>
              <span className="stat-value score-value">{gb.score}</span>
            </div>
            <div className="stat">
              <span className="stat-label">{t.game.duration}</span>
              <span className="stat-value">{formatTime(gb.elapsed)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">{t.game.errorCount}</span>
              <span className="stat-value">{gb.mistakes}</span>
            </div>
            <div className="stat">
              <span className="stat-label">{t.game.hintCount}</span>
              <span className="stat-value">{gb.hintsUsed}</span>
            </div>
            <div className="stat">
              <span className="stat-label">{t.common.difficulty}</span>
              <span className="stat-value">
                {t.difficulties[difficulty]}
              </span>
            </div>
          </div>
          <div className="finish-actions">
            <button onClick={() => startGame(difficulty)}>{t.game.sameLevel}</button>
            <button onClick={() => setGameState('menu')}>{t.game.changeDifficulty}</button>
            <button className="coach-btn" onClick={() => {
              if (!user) { navigate('/auth'); return; }
              setShowCoach(true);
            }}>🧠 {t.coach.title}{!user?.is_premium ? ' 🔒' : ''}</button>
          </div>
          <div style={{ marginTop: 16 }}><AdBanner slot={AD_SLOTS.gameEnd} /></div>
          {!user?.is_premium && (
            <div className="game-upsell-toast">
              <span>{t.ads.upsellToast}</span>
              <button onClick={() => navigate('/shop')}>Pro</button>
            </div>
          )}
        </div>
        <CoachModal
          open={showCoach}
          initialBoard={gb.puzzle.map(row => row.map(v => v ?? 0))}
          onClose={() => setShowCoach(false)}
          t={t}
        />
      </div>
    );
  }

  // ─── PLAYING ───
  return (
    <div className="game-page">
      {modalElement}
      <SEO path="/play" />
      <AppHeader
        onBack={() => navigate('/')}
        backLabel={t.common.backToHome}
        showAuth={false}
        showFontToggle
        extraControls={<SettingsPopup settings={settings} isOpen={gb.showSettings} onToggle={() => gb.setShowSettings(v => !v)} buttonLabel={t.room.settingsBtn} />}
      />

      <div className="game-page-body">
        <GameStatusBar
          elapsed={gb.elapsed}
          score={gb.score}
          scorePulsing={fx.scorePulsing}
          mistakes={gb.mistakes}
          maxMistakes={3}
          difficultyLabel={t.difficulties[difficulty]}
          difficultyColor={DIFF_COLORS[difficulty]}
          isPaused={gb.isPaused}
          onTogglePause={() => gb.setIsPaused(p => !p)}
          centerExtra={<ComboCounter count={combo} onExpire={() => setCombo(0)} />}
          targetTimeSlot={isRanked && targetSeconds != null ? (
            <span className="ranked-target-wrap">
              <button
                type="button"
                className="diff-badge ranked-target-chip"
                title={t.game.rankedTargetTime}
                aria-label={t.game.rankedTargetTime}
                onClick={() => setShowRankedTargetInfo(v => !v)}
              >
                🎯 {formatTime(targetSeconds)}
              </button>
              {showRankedTargetInfo && (
                <span className="ranked-target-info" role="status">
                  {t.game.rankedTargetInfo}
                </span>
              )}
            </span>
          ) : undefined}
        />
        <div className="game-layout">
        <div className="game-board-area">
          {fx.floatingScores.map(s => (
            <div
              key={s.id}
              className="float-score"
              style={{ '--float-offset': `${s.offset}px`, '--float-row': String(s.row), '--float-col': String(s.col) } as React.CSSProperties}
            >{s.text}</div>
          ))}
          <Confetti active={fx.showConfetti} />
          <GameGrid
            board={gb.board}
            notes={gb.notes}
            showCandidates={gb.showCandidates}
            isPaused={gb.isPaused}
            getCellClass={(r, c) => { const base = gb.getCellClass(r, c); const e = fx.getCellEffectClass(r, c); return e ? `${base} ${e}` : base; }}
            getCellStyle={fx.getCellEffectStyle}
            onCellClick={(r, c) => { log({ type: 'cell_select', row: r, col: c }); gb.handleCellClick(r, c); }}
            onResumeFromPause={() => gb.setIsPaused(false)}
            onNewGameFromPause={async () => {
              const ok = await showConfirm(
                t.game.newGameConfirmMsg,
                t.game.newGameConfirmTitle,
                t.common.confirm,
                t.common.cancel,
              );
              if (ok) {
                gb.setIsPaused(false);
                startGame(difficulty);
              }
            }}
          />
          <Numpad board={gb.board} highlightDigit={gb.highlightDigit} onNumberClick={handleNumpadClick} className="solo-numpad-mobile" />
        </div>

        <div className="game-controls">
          <Numpad board={gb.board} highlightDigit={gb.highlightDigit} onNumberClick={handleNumpadClick} className="solo-numpad-desktop" />
          <div className="action-buttons">
            <div className="action-row action-row-play">
              <button
                className={`action-btn ${gb.noteMode ? 'active' : ''}`}
                onClick={() => gb.setNoteMode(!gb.noteMode)}
                disabled={gb.showCandidates}
                title={gb.noteMode ? t.game.noteOn : t.game.noteOff}
              >
                <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg></span>
                <span className="btn-label">{gb.noteMode ? t.game.noteOn : t.game.noteOff}</span>
              </button>
              <button className="action-btn" onClick={() => eraseCell(-1, -1)} title={t.game.eraseBtn}>
                <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 20H7L3 16l13-13 4 4-9.5 9.5"/><path d="M6 17l-3-3"/></svg></span>
                <span className="btn-label">{t.game.eraseBtn}</span>
              </button>
              <button
                className="action-btn hint-btn"
                onClick={getHint}
                disabled={!user?.is_premium && gb.hintsUsed >= MAX_FREE_HINTS + bonusHints && !(rewardedAvailable && !isRanked && bonusHints < MAX_REWARDED_HINTS)}
                title={user?.is_premium ? (t.coach?.unlimitedHintsHint ?? t.game.hintBtn) : t.game.hintBtn}
              >
                <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg></span>
                <span className="btn-label">{t.game.hintBtn}</span>
                <span className="hint-badge">
                  {user?.is_premium
                    ? '∞'
                    : (gb.hintsUsed >= MAX_FREE_HINTS + bonusHints && rewardedAvailable && !isRanked && bonusHints < MAX_REWARDED_HINTS
                      ? t.ads.watchAd
                      : `${Math.max(0, MAX_FREE_HINTS + bonusHints - gb.hintsUsed)}/${MAX_FREE_HINTS + bonusHints}`)}
                </span>
              </button>
              <button
                className={`action-btn ${gb.showCandidates ? 'active' : ''}`}
                onClick={toggleCandidates}
                title={gb.showCandidates ? t.game.hideCandidates : t.game.showCandidates}
              >
                <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg></span>
                <span className="btn-label">{gb.showCandidates ? t.game.hideCandidates : t.game.showCandidates}</span>
              </button>
            </div>
            <div className="action-row action-row-end">
              <button className="action-btn" onClick={() => startGame(difficulty)} title={t.common.newGame}>
                <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg></span>
                <span className="btn-label">{t.common.newGame}</span>
              </button>
              <button
                className={`action-btn solver-link${analyzed ? ' analyzed' : ''}`}
                onClick={async () => {
                  if (analyzed) { setAnalyzed(false); return; }
                  // Çevrimdışı modda tek-adım açıklama yok (backend solver gerektirir) —
                  // eski davranış: doğrudan tam-analiz onayı.
                  if (offlineMode) {
                    const ok = await showConfirm(
                      t.game.analyzeWarningSolo,
                      t.game.analyzeWarningTitle,
                      t.game.analyzeConfirm,
                      t.common.cancel,
                    );
                    if (!ok) return;
                    setAnalyzed(true);
                    const boardStr = gb.board.map(row => row.map(c => c ?? 0).join(',')).join(';');
                    navigate(`${r('solver')}?board=${encodeURIComponent(boardStr)}`);
                    return;
                  }
                  const choice = await showChoice(
                    t.game.helpMenuMsg,
                    t.game.helpMenuTitle,
                    t.game.explainStepBtn,
                    t.game.fullAnalyzeBtn,
                    t.common.cancel,
                  );
                  if (choice === 'confirm') { void explainStep(); return; }
                  if (choice !== 'extra') return;
                  setAnalyzed(true);
                  const boardStr = gb.board.map(row =>
                    row.map(c => c ?? 0).join(',')
                  ).join(';');
                  navigate(`${r('solver')}?board=${encodeURIComponent(boardStr)}`);
                }}
                title={analyzed ? '' : t.game.analyzeTooltip}
              >
                <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
                <span className="btn-label">{analyzed ? t.game.analyzedLabel : t.game.analyzeWithSolver}</span>
              </button>
            </div>
          </div>
          <div className="game-play-ad"><AdBanner slot={AD_SLOTS.gameEnd} /></div>
        </div>{/* /game-controls */}
        </div>{/* /game-layout */}
      </div>{/* /game-page-body */}
      
      {showBonusHintPrompt && (
        <div className="app-modal-overlay" onClick={() => setShowBonusHintPrompt(false)}>
          <div className="app-modal" onClick={e => e.stopPropagation()}>
            <div className="app-modal-title">{t.ads.rewardedHintTitle}</div>
            <div className="app-modal-body">
              <p>{t.ads.rewardedHintDesc}</p>
              <p>{t.ads.removeAdsHint}</p>
            </div>
            <div className="app-modal-actions app-modal-actions-stacked">
              <button className="app-modal-btn app-modal-btn-primary" onClick={handleBonusHintWatchAd} disabled={adLoading}>
                {adLoading ? t.common.loading : t.ads.watchAd}
              </button>
              <button className="app-modal-btn app-modal-btn-primary premium-modal-btn" onClick={() => navigate('/premium')}>
                {t.premium.title}
              </button>
              <button className="app-modal-btn app-modal-btn-secondary" onClick={() => setShowBonusHintPrompt(false)}>
                {t.common.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
